import React, { useState, useEffect, useCallback, useRef } from 'react';
import { UserAvatar } from './UserAvatar';
import { Camera, Loader2, X } from 'lucide-react';
import { colors } from '../constants/colors';
import { useAuth } from '../contexts/AuthContext';
import {
  sendPhoneVerification,
  verifyPhone,
  sendEmailVerification,
  verifyEmail,
  getVerificationStatus,
  setPhoneNumber,
  updateProfile,
  uploadImageToR2,
} from '../api/client';

interface VerificationModalProps {
  onComplete: () => void;
  onDismiss?: () => void;
}

function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

type Step = 'phone-input' | 'phone' | 'email' | 'profile' | 'success';

export function VerificationModal({ onComplete, onDismiss }: VerificationModalProps) {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState<Step | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Phone input state (for social login users without phone)
  const [phoneDigits, setPhoneDigits] = useState('');
  const [emailAlreadyVerified, setEmailAlreadyVerified] = useState(false);

  // Profile setup state
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const needsProfileSetup = user?.needsProfileSetup ?? false;

  const checkStatus = useCallback(async () => {
    try {
      const status = await getVerificationStatus();
      setEmailAlreadyVerified(status.emailVerified);
      if (status.phoneVerified && status.emailVerified) {
        setStep(needsProfileSetup ? 'profile' : 'success');
      } else if (!user?.phoneNumber) {
        // Social login user without phone number
        setStep('phone-input');
      } else if (!status.phoneVerified) {
        setStep('phone');
      } else if (!status.emailVerified) {
        setStep('email');
      } else {
        setStep(needsProfileSetup ? 'profile' : 'success');
      }
    } catch {
      // Check if user has no phone (social login)
      if (!user?.phoneNumber) {
        setStep('phone-input');
      }
    }
  }, [user?.phoneNumber, needsProfileSetup]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const sendCodeForStep = useCallback(async (targetStep: Step) => {
    setSending(true);
    setError(null);
    try {
      if (targetStep === 'phone') {
        await sendPhoneVerification();
      } else if (targetStep === 'email') {
        await sendEmailVerification();
      }
      setResendCooldown(60);
    } catch (err: any) {
      if (!err.message?.includes('already verified')) {
        setError(err.message || 'Failed to send code');
      }
    } finally {
      setSending(false);
    }
  }, []);

  // Auto-send code when step changes (skip null = still loading status)
  useEffect(() => {
    if (step === 'phone' || step === 'email') {
      sendCodeForStep(step);
    }
  }, [step, sendCodeForStep]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSetPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (phoneDigits.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await setPhoneNumber(`+1${phoneDigits}`);
      await refreshUser();
      setStep('phone');
    } catch (err: any) {
      setError(err.message || 'Failed to set phone number');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      if (step === 'phone') {
        await verifyPhone(code);
        setCode('');
        // Check if email is already verified (e.g. social login)
        const status = await getVerificationStatus();
        if (status.emailVerified) {
          setStep(needsProfileSetup ? 'profile' : 'success');
        } else {
          setStep('email');
        }
      } else if (step === 'email') {
        await verifyEmail(code);
        setCode('');
        setStep(needsProfileSetup ? 'profile' : 'success');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    if (resendCooldown > 0 || !step) return;
    setCode('');
    sendCodeForStep(step);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.length === 6 && !loading) {
      handleVerify();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }
    try {
      setIsUploading(true);
      setError(null);
      const url = await uploadImageToR2(file, 'avatars');
      setAvatarUrl(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleProfileSave = async () => {
    setError(null);
    if (username.length > 0 && username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    if (username && !/^[a-zA-Z0-9_]+$/.test(username)) {
      setError('Username can only contain letters, numbers, and underscores');
      return;
    }
    try {
      setProfileSaving(true);
      await updateProfile({
        username: username || undefined,
        avatarUrl: avatarUrl || undefined,
      });
      await refreshUser();
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const allSteps: Step[] = [
    ...(user?.phoneNumber ? [] : ['phone-input' as Step]),
    'phone',
    ...(emailAlreadyVerified ? [] : ['email' as Step]),
    ...(needsProfileSetup ? ['profile' as Step] : []),
    'success',
  ];

  // Show nothing while checking verification status
  if (step === null) {
    return (
      <div style={styles.overlay}>
        <div style={styles.card}>
          <p style={{ ...styles.sendingText, padding: '32px 0' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        {/* Close button — hidden on success step */}
        {step !== 'success' && onDismiss && (
          <button onClick={onDismiss} style={styles.closeButton} aria-label="Close">
            <X size={18} />
          </button>
        )}
        {/* Progress dots */}
        <div style={styles.dots}>
          {allSteps.map((s) => {
            const stepIndex = allSteps.indexOf(s);
            const currentIndex = allSteps.indexOf(step);
            const isCompleted = stepIndex < currentIndex;
            const isCurrent = s === step;
            return (
              <div
                key={s}
                style={{
                  ...styles.dot,
                  backgroundColor: isCurrent
                    ? colors.brand.blue
                    : isCompleted
                    ? '#22C55E'
                    : colors.gray[300],
                }}
              />
            );
          })}
        </div>

        {step === 'success' ? (
          <div style={styles.successContainer}>
            <div style={styles.checkmark}>&#10003;</div>
            <h2 style={styles.heading}>You're all set!</h2>
            <p style={styles.description}>Your account is verified and ready to go.</p>
            <button onClick={onComplete} style={styles.primaryButton}>
              Start Exploring
            </button>
          </div>
        ) : step === 'profile' ? (
          <>
            <h2 style={styles.heading}>Set up your profile</h2>
            <p style={styles.description}>
              Choose a username and profile picture so people can find you.
            </p>

            {error && <div style={styles.error}>{error}</div>}

            {/* Avatar Upload */}
            <div style={styles.avatarSection}>
              <div
                style={styles.avatarWrapper}
                onClick={() => fileInputRef.current?.click()}
              >
                <UserAvatar src={avatarUrl} alt="Your avatar" size={80} />
                <div style={styles.avatarOverlay}>
                  {isUploading ? (
                    <Loader2 size={22} color={colors.white} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Camera size={22} color={colors.white} />
                  )}
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <p style={styles.avatarHint}>Tap to add a photo</p>
            </div>

            {/* Username */}
            <div style={styles.usernameField}>
              <div style={styles.usernameInputWrapper}>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="username"
                  style={styles.usernameInput}
                  maxLength={50}
                  autoFocus
                />
              </div>
              <p style={styles.usernameHint}>Letters, numbers, and underscores only</p>
            </div>

            <button
              onClick={handleProfileSave}
              disabled={profileSaving || isUploading}
              style={{
                ...styles.primaryButton,
                marginTop: '16px',
                opacity: profileSaving || isUploading ? 0.5 : 1,
                cursor: profileSaving || isUploading ? 'default' : 'pointer',
              }}
            >
              {profileSaving ? 'Saving...' : 'Continue'}
            </button>

            <button
              onClick={async () => {
                await updateProfile({});
                await refreshUser();
                setStep('success');
              }}
              style={styles.resendButton}
            >
              Skip for now
            </button>
          </>
        ) : step === 'phone-input' ? (
          <>
            <h2 style={styles.heading}>Welcome to Snagg!</h2>
            <p style={styles.description}>
              Add your phone number to verify your account and get started.
            </p>

            {error && <div style={styles.error}>{error}</div>}

            <form onSubmit={handleSetPhone}>
              <div style={styles.phoneContainer}>
                <div style={styles.countryCode}>+1</div>
                <input
                  type="tel"
                  value={formatPhoneNumber(phoneDigits)}
                  onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="(555) 123-4567"
                  style={styles.phoneInput}
                  disabled={loading}
                  autoFocus
                />
              </div>

              <p style={{ fontSize: 11, color: '#6B7280', textAlign: 'center', margin: '12px 0 0' }}>
                By providing your phone number, you consent to receive a one-time SMS verification code from Snagg. Msg &amp; data rates may apply. Reply STOP to opt out. See our{' '}
                <a href="https://snagg.live/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6', textDecoration: 'underline' }}>Privacy Policy</a>{' '}for details.
              </p>

              <button
                type="submit"
                disabled={phoneDigits.length !== 10 || loading}
                style={{
                  ...styles.primaryButton,
                  marginTop: '16px',
                  opacity: phoneDigits.length === 10 && !loading ? 1 : 0.5,
                  cursor: phoneDigits.length === 10 && !loading ? 'pointer' : 'default',
                }}
              >
                {loading ? 'Saving...' : 'Continue'}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 style={styles.heading}>
              {step === 'phone' ? 'Verify your phone' : 'Verify your email'}
            </h2>
            <p style={styles.description}>
              {step === 'phone'
                ? 'We sent a 6-digit code to your phone number'
                : 'We sent a 6-digit code to your email'}
            </p>

            {error && <div style={styles.error}>{error}</div>}

            {sending ? (
              <p style={styles.sendingText}>Sending code...</p>
            ) : (
              <>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={handleKeyDown}
                  placeholder="000000"
                  style={styles.codeInput}
                  disabled={loading}
                  autoFocus
                />

                <button
                  onClick={handleVerify}
                  disabled={code.length !== 6 || loading}
                  style={{
                    ...styles.primaryButton,
                    opacity: code.length === 6 && !loading ? 1 : 0.5,
                    cursor: code.length === 6 && !loading ? 'pointer' : 'default',
                  }}
                >
                  {loading ? 'Verifying...' : 'Verify'}
                </button>

                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  style={styles.resendButton}
                >
                  {resendCooldown > 0
                    ? `Resend code in ${resendCooldown}s`
                    : 'Resend code'}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  card: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '32px',
    width: '100%',
    maxWidth: '400px',
    margin: '0 20px',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '14px',
    right: '14px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[400],
    padding: '4px',
    display: 'flex',
    zIndex: 1,
  },
  dots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '24px',
  },
  dot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  heading: {
    color: colors.ink.black,
    fontSize: '22px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '8px',
  },
  description: {
    color: colors.gray[600],
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '24px',
  },
  error: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '10px',
    color: '#dc2626',
    fontSize: '14px',
    textAlign: 'center',
    marginBottom: '16px',
  },
  sendingText: {
    color: colors.gray[400],
    fontSize: '14px',
    textAlign: 'center',
    padding: '16px 0',
  },
  codeInput: {
    width: '100%',
    textAlign: 'center',
    fontSize: '28px',
    fontWeight: '700',
    letterSpacing: '8px',
    padding: '14px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '12px',
    backgroundColor: colors.gray[100],
    outline: 'none',
    marginBottom: '16px',
    boxSizing: 'border-box',
  },
  primaryButton: {
    width: '100%',
    padding: '14px',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '12px',
  },
  resendButton: {
    width: '100%',
    padding: '8px',
    backgroundColor: 'transparent',
    color: colors.brand.blue,
    border: 'none',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  successContainer: {
    textAlign: 'center',
    padding: '16px 0',
  },
  checkmark: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#22C55E',
    color: colors.white,
    fontSize: '36px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  avatarSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
  },
  avatarWrapper: {
    position: 'relative',
    width: '88px',
    height: '88px',
    cursor: 'pointer',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '50%',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: '600',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    borderRadius: '50%',
  },
  avatarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    borderRadius: '50%',
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  avatarHint: {
    marginTop: '8px',
    fontSize: '13px',
    color: colors.gray[600],
  },
  usernameField: {
    marginBottom: '0',
  },
  usernameInputWrapper: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '12px',
    overflow: 'hidden',
  },
  usernamePrefix: {
    padding: '14px 0 14px 14px',
    fontSize: '16px',
    color: colors.gray[600],
    backgroundColor: colors.gray[100],
    borderRight: `1px solid ${colors.gray[300]}`,
    paddingRight: '10px',
  },
  usernameInput: {
    flex: 1,
    padding: '14px',
    fontSize: '16px',
    border: 'none',
    outline: 'none',
    color: colors.ink.black,
  },
  usernameHint: {
    marginTop: '6px',
    fontSize: '12px',
    color: colors.gray[600],
  },
  phoneContainer: {
    display: 'flex',
    alignItems: 'stretch',
  },
  countryCode: {
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[300]}`,
    borderRight: 'none',
    borderRadius: '12px 0 0 12px',
    padding: '14px',
    fontSize: '16px',
    color: '#666',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
  },
  phoneInput: {
    flex: 1,
    backgroundColor: colors.gray[100],
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '0 12px 12px 0',
    padding: '14px',
    fontSize: '16px',
    color: colors.ink.black,
    outline: 'none',
  },
};
